import type { CalibrationMetrics, QualityClass, QualityThresholds } from "@gazelab/shared";
import { mean, median } from "./roi.js";

export interface ValidationPointSamples {
  /** Centro del punto de validación en px CSS del viewport. */
  targetX: number;
  targetY: number;
  /** Muestras recogidas tras el tiempo de sacada (px CSS y ms). */
  samples: ReadonlyArray<{ x: number; y: number; t: number }>;
}

/**
 * Calcula métricas de validación a partir de muestras crudas por punto.
 * - Error = distancia euclídea (px CSS) entre predicción y objetivo.
 * - `meanErrorPx`/`medianErrorPx`/`percentInRoi` agrupan todas las muestras válidas.
 * - `samplesPerSecond`: muestras / duración observada (t del primer al último dato de cada punto).
 */
export function computeValidationMetrics(
  points: ReadonlyArray<ValidationPointSamples>,
  opts: { roiRadiusPx: number; viewportWidth: number; viewportHeight: number; method: string; calibrationPoints: number },
): CalibrationMetrics {
  const allErrors: number[] = [];
  let durationMs = 0;
  let totalSamples = 0;
  const perPoint = points.map((p) => {
    const errors: number[] = [];
    for (const s of p.samples) {
      if (!Number.isFinite(s.x) || !Number.isFinite(s.y)) continue;
      errors.push(Math.hypot(s.x - p.targetX, s.y - p.targetY));
    }
    totalSamples += errors.length;
    if (p.samples.length > 1) {
      const ts = p.samples.map((s) => s.t).filter(Number.isFinite);
      if (ts.length > 1) durationMs += Math.max(...ts) - Math.min(...ts);
    }
    allErrors.push(...errors);
    const inRoi = errors.filter((e) => e <= opts.roiRadiusPx).length;
    return {
      xPx: p.targetX,
      yPx: p.targetY,
      sampleCount: errors.length,
      meanErrorPx: mean(errors),
      percentInRoi: errors.length > 0 ? (inRoi / errors.length) * 100 : null,
    };
  });
  const meanErrorPx = mean(allErrors);
  const diagonal = Math.hypot(opts.viewportWidth, opts.viewportHeight);
  const inRoiAll = allErrors.filter((e) => e <= opts.roiRadiusPx).length;
  return {
    method: opts.method,
    calibrationPoints: opts.calibrationPoints,
    pointsEvaluated: perPoint.filter((p) => p.sampleCount > 0).length,
    meanErrorPx,
    medianErrorPx: median(allErrors),
    meanErrorRelative: meanErrorPx === null || !(diagonal > 0) ? null : meanErrorPx / diagonal,
    percentInRoi: allErrors.length > 0 ? (inRoiAll / allErrors.length) * 100 : null,
    roiRadiusPx: opts.roiRadiusPx,
    samplesPerSecond: durationMs > 0 ? ((totalSamples - perPoint.filter((p) => p.sampleCount > 0).length) * 1000) / durationMs : null,
    totalSamples,
    viewportWidth: opts.viewportWidth,
    viewportHeight: opts.viewportHeight,
    points: perPoint,
  };
}

/**
 * Clasifica la calidad de una validación con umbrales configurables.
 * - `fail`: sin datos suficientes, menos de 5 puntos evaluados, error medio > umbral de
 *   advertencia o tasa de muestreo menor al mínimo.
 * - `pass`: error medio ≤ umbral de aprobación y % en ROI ≥ mínimo.
 * - `warn`: cualquier otro caso.
 */
export function classifyValidation(metrics: CalibrationMetrics, t: QualityThresholds, minPoints = 5): QualityClass {
  if (metrics.meanErrorPx === null || metrics.pointsEvaluated < minPoints) return "fail";
  if (metrics.meanErrorPx > t.warnMaxMeanErrorPx) return "fail";
  if (metrics.samplesPerSecond !== null && metrics.samplesPerSecond < t.minSamplesPerSecond) return "fail";
  if (metrics.meanErrorPx <= t.passMaxMeanErrorPx && (metrics.percentInRoi ?? 0) >= t.passMinPercentInRoi) return "pass";
  return "warn";
}

const RANK: Record<QualityClass, number> = { fail: 0, warn: 1, pass: 2 };

export function meetsQuality(actual: QualityClass, minimum: "pass" | "warn"): boolean {
  return RANK[actual] >= RANK[minimum];
}

/**
 * Puntos de validación en % de pantalla, distintos de los 9 puntos de calibración
 * (10/50/90 %), para que la validación sea independiente.
 */
export const VALIDATION_POINTS_PERCENT: ReadonlyArray<[number, number]> = [
  [30, 30],
  [70, 30],
  [45, 55],
  [30, 70],
  [70, 70],
  [50, 20],
  [20, 50],
  [80, 50],
  [50, 80],
];

export const CALIBRATION_POINTS_PERCENT: ReadonlyArray<[number, number]> = [
  [10, 10],
  [50, 10],
  [90, 10],
  [10, 50],
  [50, 50],
  [90, 50],
  [10, 90],
  [50, 90],
  [90, 90],
];
